import { IonButton, IonIcon, IonInput, IonItem, IonLabel, IonModal, IonNote } from "@ionic/react";
import { checkmarkDone, locationOutline } from "ionicons/icons";
import React, { useEffect, useState } from "react";
import SelectItem from "./SelectItem";
import Map from "../pages/GoogleMap";
import { GeolocationService, getReverseGecodingAddress } from "../services/geolocation.service";
import { getDistributors } from "../services/inventoryLocation.service";

// Shop-level InventoryLocation fields shared by both the create flow
// (CreateInventoryLocation.tsx) and the edit flow (EditInventoryLocation.tsx)
// -- everything except the owner identity (DealerOwnerFields, create-only)
// and the photo/GST-certificate uploads (ChannelPartnerMediaUploads.tsx).
// Fully self-contained: owns its own distributor-picker fetch/modal state,
// so callers only need to pass `formik` + `isDistributor`.
interface Props {
  formik: any;
  isDistributor: boolean;
  disabled?: boolean;
}

const ChannelPartnerFields: React.FC<Props> = ({ formik, isDistributor, disabled }) => {
  const [distributors, setDistributors] = useState<any[]>([]);
  const [showSelectDistributorModal, setShowSelectDistributorModal] = useState(false);

  useEffect(() => {
    if (!isDistributor) {
      getDistributors()
        .then((res) => setDistributors(res ?? []))
        .catch((err) => console.error("Error fetching distributors:", err));
    }
  }, [isDistributor]);

  function selectDistributor(item: any) {
    formik.setFieldValue("parentId", item.id);
    formik.setFieldValue("parentName", item.name);
    setShowSelectDistributorModal(false);
  }

  // Shared by both location-entry paths below: the quick "capture my
  // current GPS position" icon, and dragging/searching/clicking on the
  // map. Either way we store the same GeoJSON Point and re-run reverse
  // geocoding to refresh village/subDistrict/district/state/country.
  function applyShopLocation(latitude: number, longitude: number) {
    formik.setFieldValue("geoLocation", { type: "Point", coordinates: [longitude, latitude] });
    formik.setFieldTouched("geoLocation", true, false);
    getReverseGecodingAddress(latitude, longitude).then((resp) => {
      formik.setFieldValue("village", resp?.address?.village || "");
      formik.setFieldValue("subDistrict", resp?.address?.subDistrict || "");
      formik.setFieldValue("district", resp?.address?.district || "");
      formik.setFieldValue("state", resp?.address?.state || "");
      formik.setFieldValue("country", resp?.address?.country || "");
    });
  }

  function captureShopLocation() {
    if (disabled) return;
    const geoService = new GeolocationService();
    geoService.getCurrentPosition().then((geolocation) => {
      if (!geolocation) return;
      const { latitude, longitude } = geolocation.coords;
      applyShopLocation(latitude, longitude);
    });
  }

  function handleMapLocationChange(payload: { location: { lat: number; lng: number } }) {
    if (disabled) return;
    applyShopLocation(Number(payload.location.lat), Number(payload.location.lng));
  }

  // geoLocation is stored as a GeoJSON Point ({type, coordinates: [lng, lat]})
  // -- the map component (shared with Plot) works in {lat, lng}, so convert
  // at the boundary.
  const geoCoordinates = formik.values.geoLocation?.coordinates as [number, number] | undefined;
  const shopLat = geoCoordinates ? Number(geoCoordinates[1]) : undefined;
  const shopLng = geoCoordinates ? Number(geoCoordinates[0]) : undefined;

  function copyBillingToShipping() {
    formik.setFieldValue("shippingAddress", formik.values.billingAddress);
  }

  // Blocks characters Instagram/Facebook usernames never allow (spaces,
  // "@", etc.) as the user types, rather than only flagging them after the
  // fact -- people often type the "@" out of habit even though it's just a
  // display convention, not part of the handle. Structural rules (can't
  // start/end with a period, no "..") are left to the Yup validator instead
  // of stripped live, since disallowing them mid-type would fight a user
  // who's about to type the next character anyway (e.g. "john" -> "john.").
  function onSocialHandleChange(e: any) {
    const sanitized = (e?.detail?.value ?? "").replace(/[^a-zA-Z0-9._]/g, "");
    formik.setFieldValue("socialHandle", sanitized);
  }

  return (
    <>
      {!isDistributor && (
        <>
          <IonItem>
            <IonLabel position="stacked">Distributor</IonLabel>
            <IonInput
              readonly
              disabled={disabled}
              value={formik.values.parentName}
              placeholder="Select distributor"
              onClick={() => !disabled && setShowSelectDistributorModal(true)}
            ></IonInput>
          </IonItem>
          {formik.touched.parentId && formik.errors.parentId ? (
            <div className="errorMsg">{formik.errors.parentId}</div>
          ) : null}
        </>
      )}

      <IonItem>
        <IonLabel position="stacked">Business / Shop Name</IonLabel>
        <IonInput
          id="businessName"
          name="businessName"
          disabled={disabled}
          value={formik.values.businessName}
          onIonChange={formik.handleChange}
        ></IonInput>
      </IonItem>
      {formik.touched.businessName && formik.errors.businessName ? (
        <div className="errorMsg">{formik.errors.businessName}</div>
      ) : null}

      <IonItem>
        <IonLabel position="stacked">Business WhatsApp No.</IonLabel>
        <IonInput
          id="whatsappNo"
          name="whatsappNo"
          type="tel"
          disabled={disabled}
          value={formik.values.whatsappNo}
          onIonChange={formik.handleChange}
        ></IonInput>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Business Email Id</IonLabel>
        <IonInput
          id="email"
          name="email"
          type="email"
          disabled={disabled}
          value={formik.values.email}
          onIonChange={formik.handleChange}
        ></IonInput>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">GST No.</IonLabel>
        <IonInput id="gstNo" name="gstNo" disabled={disabled} value={formik.values.gstNo} onIonChange={formik.handleChange}></IonInput>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Instagram / Facebook User ID</IonLabel>
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <IonNote class="secondary-text" style={{ marginRight: 4 }}>
            @
          </IonNote>
          <IonInput
            id="socialHandle"
            name="socialHandle"
            maxlength={30}
            disabled={disabled}
            value={formik.values.socialHandle}
            placeholder="username"
            onIonChange={onSocialHandleChange}
            onIonBlur={formik.handleBlur}
          ></IonInput>
        </div>
      </IonItem>
      {formik.touched.socialHandle && formik.errors.socialHandle ? (
        <div className="errorMsg">{formik.errors.socialHandle}</div>
      ) : null}

      <IonItem>
        <IonLabel>
          Shop GPS Location
          {formik.values.geoLocation && (
            <p style={{ color: "var(--ion-color-success)" }}>
              <IonIcon icon={checkmarkDone} style={{ verticalAlign: "middle" }} /> Captured
            </p>
          )}
        </IonLabel>
        <IonIcon
          icon={locationOutline}
          slot="end"
          onClick={captureShopLocation}
          style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 }}
        />
      </IonItem>
      {formik.touched.geoLocation && formik.errors.geoLocation ? (
        <div className="errorMsg">{formik.errors.geoLocation as string}</div>
      ) : null}
      <div
        style={{
          height: 280,
          width: "100%",
          padding: "0 16px 8px",
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Map
          lat={shopLat}
          lng={shopLng}
          zoom={shopLat !== undefined ? 17 : 6}
          defaultEditable
          hideBoundaryTool
          onPlotsLocationChange={handleMapLocationChange}
        />
      </div>
      {formik.values.village && (
        <IonItem lines="none">
          <IonLabel class="secondary-text">
            {formik.values.village}, {formik.values.subDistrict}, {formik.values.district}, {formik.values.state}
          </IonLabel>
        </IonItem>
      )}

      <IonItem>
        <IonLabel position="stacked">Billing Address</IonLabel>
        <IonInput
          id="billingAddress"
          name="billingAddress"
          disabled={disabled}
          value={formik.values.billingAddress}
          onIonChange={formik.handleChange}
        ></IonInput>
      </IonItem>
      {formik.touched.billingAddress && formik.errors.billingAddress ? (
        <div className="errorMsg">{formik.errors.billingAddress}</div>
      ) : null}

      <IonItem>
        <IonLabel position="stacked">Shipping Address</IonLabel>
        <IonInput
          id="shippingAddress"
          name="shippingAddress"
          disabled={disabled}
          value={formik.values.shippingAddress}
          onIonChange={formik.handleChange}
        ></IonInput>
        <IonButton slot="end" size="small" fill="clear" disabled={disabled} onClick={copyBillingToShipping}>
          Same as Billing
        </IonButton>
      </IonItem>
      {formik.touched.shippingAddress && formik.errors.shippingAddress ? (
        <div className="errorMsg">{formik.errors.shippingAddress}</div>
      ) : null}

      {showSelectDistributorModal && (
        <IonModal isOpen={showSelectDistributorModal}>
          <SelectItem
            label="Select Distributor"
            items={distributors}
            labelKey="name"
            valueKey="id"
            setItem={selectDistributor}
            selectedValue=""
            onClose={setShowSelectDistributorModal}
          />
        </IonModal>
      )}
    </>
  );
};

export default ChannelPartnerFields;
